import { useEffect, useRef } from 'react';
import maplibregl from 'maplibre-gl';
import { Task } from '../types';
import { Task3DLayerAbsolute as Task3DLayer } from '../components/map/Task3DLayerAbsolute';

interface UseTask3DProps {
  map: maplibregl.Map | null;
  task: Task | undefined;
  enabled: boolean;
  is3D: boolean;
}

export const useTask3D = ({ map, task, enabled, is3D }: UseTask3DProps) => {
  const task3DLayerRef = useRef<Task3DLayer | null>(null);
  const layerAddedRef = useRef<boolean>(false);

  useEffect(() => {
    if (!map || !task || !enabled) {
      return;
    }

    const layerId = 'task-3d-layer';

    // Create or update the 3D task layer
    if (!task3DLayerRef.current) {
      task3DLayerRef.current = new Task3DLayer(layerId, task);
    } else {
      task3DLayerRef.current.updateTask(task);
    }

    // Add layer when in 3D mode
    if (is3D && !layerAddedRef.current) {
      // Check if layer already exists
      if (!map.getLayer(layerId)) {
        try {
          const layer = task3DLayerRef.current.getLayer();
          map.addLayer(layer);
          layerAddedRef.current = true;
        } catch (error) {
          console.error('[useTask3D] Failed to add layer:', error);
        }
      } else {
      }
    }

    // Toggle visibility based on 3D mode
    if (task3DLayerRef.current) {
      task3DLayerRef.current.setVisibility(is3D);
    }

    // Remove layer when not in 3D mode
    if (!is3D && layerAddedRef.current) {
      if (map.getLayer(layerId)) {
        map.removeLayer(layerId);
        layerAddedRef.current = false;
      }
    }

    return () => {
      // Cleanup on unmount
      if (map && layerAddedRef.current && map.getLayer(layerId)) {
        try {
          map.removeLayer(layerId);
        } catch (e) {
          console.warn('Failed to remove task 3D layer:', e);
        }
      }
      task3DLayerRef.current = null;
      layerAddedRef.current = false;
    };
  }, [map, task, enabled, is3D]);

  return task3DLayerRef.current;
};